import { Check, ChevronsUpDown, Droplet } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/arc/popover/popover";
import styles from "./shell.module.css";

export interface CompanyOption {
  id: string;
  name: string;
  slug: string;
  /** The company's logo; the Droplet mark (letters in the menu) when there is none or it fails to load. */
  logoSrc?: string | null;
  /** Under the name in the menu; the slug by default. */
  detail?: string;
  /** In place of the Droplet mark when there is no logo (the customer app: a building or a person). */
  icon?: ReactNode;
}

export interface CompanySwitcherProps {
  companies: CompanyOption[];
  currentId: string;
  /** Under the company name, e.g. "Admin console". */
  product: string;
  /** Called with the chosen company; the switcher closes when it settles. */
  onSwitch: (companyId: string) => void | Promise<unknown>;
  /** The rail shows the mark only. */
  compact?: boolean;
  /** Above the menu's list. */
  heading?: string;
  /** Under the menu's list: what switching does. */
  note?: string;
}

const markOf = (company: CompanyOption) => company.slug.slice(0, 3).toUpperCase();

/**
 * The company at the top of the sidebar (Company Switcher, menu `Bow7O`). With one company it's
 * a plain label: no chevron, no menu.
 */
export function CompanySwitcher({
  companies,
  currentId,
  product,
  onSwitch,
  compact = false,
  heading = "Your companies",
  note = "Switching reopens the console in that company.",
}: CompanySwitcherProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  // logos that failed to load: their mark shows instead
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());
  const failed = (src: string) => setBroken((was) => new Set(was).add(src));
  const logoOf = (company: CompanyOption | undefined) =>
    company?.logoSrc && !broken.has(company.logoSrc) ? company.logoSrc : null;
  const current = companies.find((c) => c.id === currentId) ?? companies[0];
  const several = companies.length > 1;
  const logo = logoOf(current);

  const face = (
    <>
      {logo ? (
        <img className={styles.companyLogo} src={logo} alt="" onError={() => failed(logo)} />
      ) : (
        <span className={styles.companyMark} aria-hidden="true">
          {current?.icon ?? <Droplet size={18} />}
        </span>
      )}
      {compact ? null : (
        <span className={styles.companyText}>
          <span className={styles.companyName}>{current?.name}</span>
          <span className={styles.companyProduct}>{product}</span>
        </span>
      )}
      {several && !compact ? <ChevronsUpDown className={styles.chevron} size={16} aria-hidden="true" /> : null}
    </>
  );

  if (!several) {
    return (
      <div
        className={styles.switcher}
        data-compact={compact ? "" : undefined}
        title={compact ? current?.name : undefined}
      >
        {face}
      </div>
    );
  }

  const choose = async (id: string) => {
    if (id === currentId) return setOpen(false);
    setPending(id);
    try {
      await onSwitch(id);
      setOpen(false);
    } finally {
      setPending(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={styles.switcher}
          data-compact={compact ? "" : undefined}
          aria-label={`${current?.name}, ${product}. Switch company`}
        >
          {face}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={styles.companyMenu}>
        <p className={styles.menuHeader}>{heading}</p>
        <ul className={styles.menuList}>
          {companies.map((company) => {
            const isCurrent = company.id === currentId;
            const itemLogo = logoOf(company);
            return (
              <li key={company.id}>
                <button
                  type="button"
                  className={styles.menuCompany}
                  aria-current={isCurrent ? "true" : undefined}
                  aria-busy={pending === company.id || undefined}
                  disabled={pending !== null}
                  onClick={() => choose(company.id)}
                >
                  {itemLogo ? (
                    <img className={styles.menuLogo} src={itemLogo} alt="" onError={() => failed(itemLogo)} />
                  ) : (
                    <span className={styles.menuMark} aria-hidden="true">
                      {markOf(company)}
                    </span>
                  )}
                  <span className={styles.companyText}>
                    <span className={styles.companyName}>{company.name}</span>
                    <span className={styles.companyProduct}>{company.detail ?? company.slug}</span>
                  </span>
                  {isCurrent ? <Check size={16} className={styles.menuCheck} aria-hidden="true" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
        {note ? <p className={styles.menuNote}>{note}</p> : null}
      </PopoverContent>
    </Popover>
  );
}
